import {
  iReviewData, iReview, iLike, iComment,
} from '@/entities/review/review.types';
import { Review } from '@/entities/review/review';
import { Like } from '@/entities/review/like';
import { Comment } from '@/entities/review/comment';
import { Share } from '@/entities/review/share';
import { iReviewService } from './review.types';
import { iFirestoreReviewModel } from '../firestore/firestore.types';
import { iUidService } from '../uid/uid.types';
import { iDateService } from '../date/date.types';

const REVIEWS_PER_PAGE = 15;

export class ReviewService implements iReviewService {
  private db: iFirestoreReviewModel;

  private uidService: iUidService;

  private dateService: iDateService;

  constructor(
    db: iFirestoreReviewModel,
    uidService: iUidService,
    dateService: iDateService,
  ) {
    this.db = db;
    this.uidService = uidService;
    this.dateService = dateService;
  }

  async create(reviewData: iReviewData): Promise<iReview> {
    const id: string = this.uidService.generateV4();
    const createdAt: number = this.dateService.now();
    const {
      text, rating, gameId, gameName, userId, gamertag,
    } = reviewData;

    const review: iReview = new Review(
      id,
      text,
      rating,
      gameId,
      gameName,
      userId,
      gamertag,
      createdAt,
    );

    await this.db.create(review);

    return review;
  }

  getReviewsByGameId(gameId: number): Promise<iReview[]> {
    return this.db.findByGameId(gameId);
  }

  async like(review: iReview, userId: string): Promise<iReview> {
    const id: string = this.uidService.generateV4();
    const createdAt: number = this.dateService.now();

    const like: iLike = new Like(id, userId, createdAt);

    await this.db.like(review.id, like);

    review.likes.push(like);

    return review;
  }

  async unlike(review: iReview, userId: string): Promise<iReview> {
    await this.db.unlike(review.id, userId);

    const likes = review.likes.filter((like) => like.userId !== userId);
    const newReview = new Review(
      review.id,
      review.text,
      review.rating,
      review.gameId,
      review.gameName,
      review.userId,
      review.gamertag,
      review.createdAt,
      likes,
    );

    return newReview;
  }

  async comment(review: iReview, userId: string, gamertag: string, text: string): Promise<iReview> {
    const id: string = this.uidService.generateV4();
    const createdAt: number = this.dateService.now();

    const comment: iComment = new Comment(id, userId, gamertag, text, createdAt);

    await this.db.comment(review.id, comment);

    review.comments.unshift(comment);

    return review;
  }

  getCommentsByReviewId(reviewId: string): Promise<iComment[]> {
    return this.db.getCommentsByReviewId(reviewId);
  }

  getReviewsForFeed(lastReview: iReview | null): Promise<iReview[]> {
    return this.db.getAllReviews(lastReview, REVIEWS_PER_PAGE);
  }

  getReviewsByUser(userId: string, lastReview: iReview | null): Promise<iReview[]> {
    return this.db.getAllReviewsByUserId(userId, lastReview, REVIEWS_PER_PAGE);
  }

  async share(review: iReview, userId: string, gamertag: string): Promise<iReview> {
    const id: string = this.uidService.generateV4();
    const createdAt: number = this.dateService.now();

    let share;
    if (review.share) {
      share = new Share(
        review.share.reviewId,
        review.share.reviewUserId,
        review.share.reviewGamertag,
      );
    } else {
      share = new Share(review.id, review.userId, review.gamertag);
    }

    const newReview: iReview = new Review(
      id,
      review.text,
      review.rating,
      review.gameId,
      review.gameName,
      userId,
      gamertag,
      createdAt,
      [],
      [],
      share,
    );

    await this.db.create(newReview);

    return newReview;
  }
}
